import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { FadeIn } from './ui/Section';
import { CreditCard, ShoppingBag, AlertTriangle } from 'lucide-react';
const POWER_HOOKS = ['¿Otra vez compraste algo que no necesitabas?', '¿Cuánto llevas gastado este mes sin darte cuenta?', '¿Te da miedo abrir tu app del banco?'];
export function Hero() {
  const [hookIndex, setHookIndex] = useState(0);
  const [wastedAmount, setWastedAmount] = useState(0);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Rotate power hooks
  useEffect(() => {
    const interval = setInterval(() => {
      setHookIndex(prev => (prev + 1) % POWER_HOOKS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);
  // Animated money counter
  useEffect(() => {
    const target = 4800;
    const duration = 2500;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setWastedAmount(target);
        clearInterval(timer);
      } else {
        setWastedAmount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);
  return <header className="w-full min-h-screen flex flex-col justify-center bg-[#FFF5F0]/50 px-6 py-16 md:py-24 relative overflow-hidden" data-id="element-70">
      <div className="max-w-6xl mx-auto w-full relative" data-id="element-71">
        {/* Main grid: Text left, Visual right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-72">
          {/* LEFT: Power copy + CTA */}
          <div className="space-y-8 text-left" data-id="element-73">
            {/* Rotating power hook */}
            <FadeIn data-id="element-74">
              <div className="h-20 md:h-24 flex items-center" data-id="element-75">
                <AnimatePresence mode="wait" data-id="element-76">
                  <motion.p key={hookIndex} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.5
                }} className="text-lg md:text-xl text-[#C4467E] font-medium tracking-wide uppercase" data-id="element-77">
                    {POWER_HOOKS[hookIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </FadeIn>

            {/* Main headline */}
            <FadeIn delay={0.15} data-id="element-78">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#3D1F2E] leading-[1.1] tracking-tight" data-id="element-79">
                Deja de tirar <br data-id="element-80" />
                <span className="italic text-[#C4467E]" data-id="element-81">tu dinero</span> <br data-id="element-82" />a
                la basura.
              </h1>
            </FadeIn>

            {/* Animated money counter — pain amplifier */}
            <FadeIn delay={0.3} data-id="element-83">
              <motion.div className="bg-[#3D1F2E]/90 backdrop-blur-sm rounded-2xl p-5 md:p-6 inline-block" animate={{
              boxShadow: ['0 0 0px rgba(196,70,126,0)', '0 0 30px rgba(196,70,126,0.3)', '0 0 0px rgba(196,70,126,0)']
            }} transition={{
              duration: 2.5,
              repeat: Infinity
            }} data-id="element-84">
                <p className="text-[#E8D5DC] text-sm uppercase tracking-widest mb-1" data-id="element-85">
                  Promedio que una mujer gasta en compras impulsivas
                </p>
                <div className="flex items-baseline gap-1" data-id="element-86">
                  <span className="text-4xl md:text-5xl font-serif font-bold text-white" data-id="element-87">
                    ${wastedAmount.toLocaleString()}MXN
                  </span>
                  <span className="text-[#C4467E] text-lg font-medium" data-id="element-88">
                    /mes
                  </span>
                </div>
                <p className="text-[#7A5C6B] text-xs mt-1" data-id="element-89">
                  💸 Eso son $57,600MXN al año que podrían ser tuyos
                </p>
              </motion.div>
            </FadeIn>

            {/* Quick pain points — visual, not paragraphs */}
            <FadeIn delay={0.45} data-id="element-90">
              <div className="flex flex-col gap-3" data-id="element-91">
                {[{
                icon: <ShoppingBag className="w-5 h-5" data-id="element-92" />,
                text: '"Solo voy a ver..." y terminas comprando'
              }, {
                icon: <CreditCard className="w-5 h-5" data-id="element-93" />,
                text: 'La culpa después del cargo. Cada. Vez.'
              }, {
                icon: <AlertTriangle className="w-5 h-5" data-id="element-94" />,
                text: '"Ahora sí me controlo" — hasta que vuelve a pasar'
              }].map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6 + i * 0.15,
                duration: 0.4
              }} className="flex items-center gap-3 text-[#7A5C6B]" data-id="element-95">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#FDE8F0]/80 flex items-center justify-center text-[#C4467E]" data-id="element-96">
                      {item.icon}
                    </div>
                    <span className="text-base md:text-lg" data-id="element-97">{item.text}</span>
                  </motion.div>)}
              </div>
            </FadeIn>

            {/* CTA */}
            <FadeIn delay={0.7} data-id="element-98">
              <div className="flex flex-col sm:flex-row items-start gap-4 pt-4" data-id="element-99">
                <Button size="lg" withArrow onClick={scrollToPricing} data-id="element-100">
                  Quiero recuperar mi control
                </Button>
                <p className="text-xs text-[#7A5C6B] self-center" data-id="element-101">
                  Sistema de 14 días · Solo $129MXN · Garantía 30 días
                </p>
              </div>
            </FadeIn>
          </div>

          {/* RIGHT: Visual power — images + product */}
          <FadeIn delay={0.3} data-id="element-102">
            <div className="relative flex justify-center lg:justify-end" data-id="element-103">
              {/* Main lifestyle image */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }} className="relative" data-id="element-104">
                <img src="https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=750&fit=crop&crop=center" alt="Mujer con bolsas de compras" className="w-full max-w-sm md:max-w-md rounded-3xl shadow-2xl object-cover" style={{
                aspectRatio: '3/4'
              }} data-id="element-105" />
                {/* Overlay gradient */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#3D1F2E]/60 via-transparent to-transparent" data-id="element-106" />

                {/* Overlay text on image */}
                <motion.div className="absolute bottom-6 left-6 right-6" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1,
                duration: 0.6
              }} data-id="element-107">
                  <p className="text-white font-serif text-xl md:text-2xl italic leading-snug" data-id="element-108">
                    "No te falta disciplina. <br data-id="element-109" />
                    <span className="text-[#FDE8F0] font-bold not-italic" data-id="element-110">
                      Te falta un sistema."
                    </span>
                  </p>
                </motion.div>

                {/* Floating product card */}
                <motion.div className="absolute -bottom-8 -left-6 md:-left-12 bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-[#E8D5DC]" initial={{
                opacity: 0,
                y: 30,
                rotate: -3
              }} animate={{
                opacity: 1,
                y: 0,
                rotate: -3
              }} transition={{
                delay: 1.2,
                duration: 0.6
              }} data-id="element-111">
                  <img src="https://cdn.magicpatterns.com/uploads/h241stnUBcHgWd2SzzrFSw/Anti_impulso_cover.png" alt="Anti-Impulso — Ebook" className="w-28 md:w-36 rounded-lg" data-id="element-112" />
                  <p className="text-[#3D1F2E] font-bold text-sm mt-2 text-center" data-id="element-113">
                    Anti-Impulso
                  </p>
                  <p className="text-[#C4467E] font-serif text-lg font-bold text-center" data-id="element-114">
                    $129MXN
                  </p>
                </motion.div>

                {/* Floating alert badge */}
                <motion.div className="absolute top-6 -right-4 md:-right-8 bg-[#C4467E] text-white rounded-full px-4 py-2 shadow-lg" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 1.4,
                duration: 0.4,
                type: 'spring'
              }} data-id="element-115">
                  <p className="text-xs font-bold uppercase tracking-wider" data-id="element-116">
                    🔥 14 días
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </FadeIn>
        </div>
      </div>
    </header>;
}